import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore - Leaflet runtime import is already used by Roddo Big Blue.
import L from 'leaflet';
import type { Tournament } from '../../types';
import {
  getTournamentMapLatLng,
  getTournamentDiscoveryPriority,
  isWorldwideTournament,
} from '../../utils/tournamentDiscovery';

type MapBounds = { south: number; west: number; north: number; east: number };

type Props = {
  tournaments: Tournament[];
  isDarkMode?: boolean;
  onOpenTournament: (tournament: Tournament) => void;
  onSearchArea: (bounds: MapBounds) => void;
  areaFilterActive?: boolean;
  onClearArea: () => void;
};

const boundsToPlain = (bounds: any): MapBounds => ({
  south: Number(bounds.getSouth()),
  west: Number(bounds.getWest()),
  north: Number(bounds.getNorth()),
  east: Number(bounds.getEast()),
});


const BROWSER_GEO_CACHE_KEY = 'roddo.browserGeo.lastKnown.v1';

function readCachedAnglerLocation(): { lat: number; lng: number } | null {
  try {
    const raw = window.localStorage.getItem(BROWSER_GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng ?? parsed?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}


type WorldwideStatusVisual = {
  label: 'OPEN' | 'LIVE' | 'FINISHED' | 'SOON';
  chipClass: string;
  dotClass: string;
  rowClass: string;
  accentClass: string;
};

function compactCountdownDuration(target: number, now: number) {
  if (!Number.isFinite(target) || target <= 0) return '';
  const delta = target - now;
  if (delta <= 0) return 'now';
  const minutes = Math.max(1, Math.floor(delta / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function tournamentCountdownLabel(tournament: Tournament, now: number) {
  const status = String((tournament as any)?.status || '').trim().toLowerCase();
  if (['finalized', 'completed', 'archived', 'cancelled', 'rejected'].includes(status)) return '';
  if (['active', 'live'].includes(status)) {
    const value = compactCountdownDuration(Number((tournament as any)?.endDate || 0), now);
    return value ? `Ends ${value}` : '';
  }
  const closesAt = Number((tournament as any)?.registrationClosesAt || 0);
  if (status === 'registration_open' && closesAt > now) {
    const value = compactCountdownDuration(closesAt, now);
    return value ? `Closes ${value}` : '';
  }
  const value = compactCountdownDuration(Number((tournament as any)?.startDate || 0), now);
  return value ? `Starts ${value}` : '';
}

function worldwideStatusVisual(tournament: Tournament): WorldwideStatusVisual {
  const status = String((tournament as any)?.status || '').trim().toLowerCase();
  const now = Date.now();
  const endDate = Number((tournament as any)?.endDate || 0);
  const registrationClosesAt = Number((tournament as any)?.registrationClosesAt || 0);

  if (
    ['finalized', 'completed', 'archived', 'cancelled', 'rejected'].includes(status) ||
    (endDate > 0 && endDate <= now)
  ) {
    return {
      label: 'FINISHED',
      chipClass:
        'border-rose-500/35 bg-rose-500/10 text-rose-700 dark:border-rose-300/25 dark:bg-rose-400/10 dark:text-rose-200',
      dotClass: 'bg-rose-500 dark:bg-rose-300',
      rowClass:
        'border-rose-500/10 bg-[linear-gradient(135deg,rgba(244,63,94,.055),rgba(255,255,255,.84))] dark:border-rose-300/10 dark:bg-[linear-gradient(135deg,rgba(244,63,94,.09),rgba(21,29,49,.88))]',
      accentClass: 'bg-rose-500/70 dark:bg-rose-300/70',
    };
  }

  if (['active', 'live'].includes(status)) {
    return {
      label: 'LIVE',
      chipClass:
        'border-emerald-500/35 bg-emerald-500/10 text-emerald-700 dark:border-emerald-300/25 dark:bg-emerald-400/10 dark:text-emerald-200',
      dotClass: 'bg-emerald-500 dark:bg-emerald-300',
      rowClass:
        'border-emerald-500/12 bg-[linear-gradient(135deg,rgba(16,185,129,.06),rgba(255,255,255,.88))] dark:border-emerald-300/10 dark:bg-[linear-gradient(135deg,rgba(16,185,129,.09),rgba(21,29,49,.9))]',
      accentClass: 'bg-emerald-500/75 dark:bg-emerald-300/75',
    };
  }

  if (
    status === 'registration_open' &&
    (!registrationClosesAt || registrationClosesAt > now)
  ) {
    return {
      label: 'OPEN',
      chipClass:
        'border-amber-500/45 bg-amber-400/14 text-amber-800 dark:border-amber-300/35 dark:bg-amber-300/10 dark:text-[#FFE4A1]',
      dotClass: 'bg-amber-500 dark:bg-amber-300',
      rowClass:
        'border-amber-500/14 bg-[linear-gradient(135deg,rgba(245,158,11,.065),rgba(255,255,255,.9))] dark:border-amber-300/10 dark:bg-[linear-gradient(135deg,rgba(245,158,11,.10),rgba(21,29,49,.9))]',
      accentClass: 'bg-amber-500/80 dark:bg-amber-300/80',
    };
  }

  return {
    label: 'SOON',
    chipClass:
      'border-slate-400/30 bg-slate-500/8 text-slate-600 dark:border-white/15 dark:bg-white/[.055] dark:text-white/65',
    dotClass: 'bg-slate-400 dark:bg-white/45',
    rowClass:
      'border-slate-400/12 bg-white/80 dark:border-white/8 dark:bg-[#151D31]/88',
    accentClass: 'bg-slate-400/55 dark:bg-white/30',
  };
}

function makeMarkerIcon(count: number, dark: boolean) {
  const safeCount = Math.max(1, Math.min(99, Math.round(Number(count) || 1)));
  const countText = safeCount > 99 ? '99+' : String(safeCount);

  return (L as any).divIcon({
    className: 'roddo-tournament-dock-map-icon',
    iconSize: [52, 62],
    iconAnchor: [26, 58],
    popupAnchor: [0, -48],
    html: `
      <div style="position:relative;width:52px;height:62px;overflow:visible;filter:drop-shadow(0 4px 7px rgba(15,23,42,.16));">
        <div style="position:absolute;left:50%;top:18px;width:58px;height:58px;border-radius:999px;transform:translate(-50%,-50%);background:radial-gradient(circle,rgba(246,190,50,.28) 0%,rgba(255,255,255,0) 68%);"></div>
        <div style="position:absolute;left:50%;top:4px;width:48px;height:48px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,#FFD45F 0%,#C9830D 84%);border:2px solid rgba(255,255,255,.98);box-shadow:0 7px 16px rgba(95,59,8,.24),0 0 0 4px rgba(246,190,50,.24);">
          <div style="position:absolute;inset:5px;border-radius:999px;background:${dark ? 'linear-gradient(145deg,#1A2438 0%,#0F1728 100%)' : 'linear-gradient(145deg,#243552 0%,#142238 100%)'};display:grid;place-items:center;transform:rotate(45deg);overflow:hidden;box-shadow:inset 0 0 0 1px rgba(255,255,255,.10),0 2px 5px rgba(15,23,42,.20);">
            <svg viewBox="0 0 24 24" aria-hidden="true" style="width:22px;height:22px;display:block;overflow:visible;">
                <path d="M8 4.75H16V8.5C16 10.709 14.209 12.5 12 12.5C9.791 12.5 8 10.709 8 8.5V4.75Z" fill="rgba(255,215,106,.18)" stroke="#FFD76A" stroke-width="1.8" stroke-linejoin="round"/>
                <path d="M8 6H5.75C5.336 6 5 6.336 5 6.75V7.5C5 9.433 6.567 11 8.5 11M16 6H18.25C18.664 6 19 6.336 19 6.75V7.5C19 9.433 17.433 11 15.5 11" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 12.5V16M9.5 16H14.5M9 19H15" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
          </div>
        </div>
        ${count > 1 ? `<div style="position:absolute;right:-4px;top:-3px;z-index:4;min-width:20px;height:20px;padding:0 4px;border-radius:999px;display:grid;place-items:center;background:${dark ? '#151D31' : '#FFFFFF'};color:${dark ? '#FFE4A1' : '#9B680A'};border:2px solid ${dark ? '#7A6940' : '#D6A52B'};font:950 8px/1 system-ui,-apple-system,sans-serif;box-shadow:0 2px 6px rgba(15,23,42,.22);">${countText}</div>` : ''}
      </div>
    `,
  });
}

export default function TournamentDiscoveryMap({
  tournaments,
  isDarkMode = false,
  onOpenTournament,
  onSearchArea,
  areaFilterActive = false,
  onClearArea,
}: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const rowsRef = useRef<Tournament[]>(tournaments);
  const openRef = useRef(onOpenTournament);
  const fittedRef = useRef(false);
  const anglerAnchorRef = useRef<{ lat: number; lng: number } | null>(
    typeof window !== 'undefined' ? readCachedAnglerLocation() : null,
  );
  const [worldwideMenuOpen, setWorldwideMenuOpen] = useState(false);
  const [searchFeedback, setSearchFeedback] = useState<'idle' | 'searching' | 'found' | 'empty'>('idle');
  const [searchResultCount, setSearchResultCount] = useState(0);
  const searchFeedbackTimerRef = useRef<number | null>(null);
  const worldwideMenuRef = useRef<HTMLDivElement | null>(null);

  const worldwideRows = useMemo(
    () =>
      [...tournaments]
        .filter((tournament) => isWorldwideTournament(tournament))
        .sort(
          (a, b) =>
            getTournamentDiscoveryPriority(b) - getTournamentDiscoveryPriority(a),
        ),
    [tournaments],
  );

  rowsRef.current = tournaments;
  openRef.current = onOpenTournament;

  useEffect(() => {
    return () => {
      if (searchFeedbackTimerRef.current !== null) {
        window.clearTimeout(searchFeedbackTimerRef.current);
      }
    };
  }, []);

  const finishAreaSearch = useCallback(() => {
    const localCount = (rowsRef.current || []).filter(
      (row) => !isWorldwideTournament(row) && !!getTournamentMapLatLng(row),
    ).length;
    setSearchResultCount(localCount);
    setSearchFeedback(localCount > 0 ? 'found' : 'empty');
  }, []);

  const clearAreaSearch = useCallback(() => {
    if (searchFeedbackTimerRef.current !== null) {
      window.clearTimeout(searchFeedbackTimerRef.current);
      searchFeedbackTimerRef.current = null;
    }
    setSearchFeedback('idle');
    setSearchResultCount(0);
    setWorldwideMenuOpen(false);
    onClearArea();
  }, [onClearArea]);

  useEffect(() => {
    if (!worldwideMenuOpen) return;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const root = worldwideMenuRef.current;
      const target = event.target as Node | null;
      if (!root || !target || root.contains(target)) return;
      setWorldwideMenuOpen(false);
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setWorldwideMenuOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [worldwideMenuOpen]);

  const redraw = useCallback(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    const zoom = Number(map.getZoom?.() ?? 8);
    const gridSize = zoom >= 12 ? 46 : zoom >= 9 ? 58 : 72;
    const groups = new Map<string, Tournament[]>();

    for (const tournament of rowsRef.current || []) {
      // Worldwide tournaments are rendered as a viewport-fixed WORLD hub below.
      // They intentionally never become geographic Leaflet markers, which keeps
      // them from drifting/pushing offscreen as the discovery map is panned.
      if (isWorldwideTournament(tournament)) continue;
      const point = getTournamentMapLatLng(tournament);
      if (!point) continue;
      const projected = map.project([point.lat, point.lng], zoom);
      const key = `${Math.round(projected.x / gridSize)}:${Math.round(projected.y / gridSize)}`;
      const bucket = groups.get(key) || [];
      bucket.push(tournament);
      groups.set(key, bucket);
    }

    groups.forEach((bucket) => {
      const points = bucket
        .map((row) => getTournamentMapLatLng(row))
        .filter(Boolean) as Array<{lat:number;lng:number}>;
      if (!points.length) return;
      const lat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
      const lng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
      const sorted = [...bucket].sort(
        (a, b) => getTournamentDiscoveryPriority(b) - getTournamentDiscoveryPriority(a),
      );
      const lead = sorted[0];
      const status = String(lead?.status || '').toLowerCase();
      const marker = (L as any).marker([lat, lng], {
        icon: makeMarkerIcon(bucket.length, isDarkMode),
        keyboard: true,
        riseOnHover: true,
      });

      if (bucket.length === 1) {
        marker.on('click', () => openRef.current?.(lead));
        const countdown = tournamentCountdownLabel(lead, Date.now());
        marker.bindTooltip?.(
          `<div style="font-weight:900">${String(lead.name || 'Tournament').replace(/[<>&"]/g, '')}</div><div style="font-size:11px;opacity:.7">${String(lead.locationLabel || '').replace(/[<>&"]/g, '')}</div>${countdown ? `<div style="margin-top:3px;font-size:10.5px;font-weight:800;color:#B77900">${countdown}</div>` : ''}`,
          { direction: 'top', offset: [0, -14] },
        );
      } else {
        marker.on('click', () => {
          const bounds = (L as any).latLngBounds(points.map((p) => [p.lat, p.lng]));
          map.fitBounds(bounds, { padding: [34, 34], maxZoom: 13, animate: true });
        });
        marker.bindTooltip?.(`${bucket.length} tournaments`, { direction: 'top', offset: [0, -14] });
      }
      marker.addTo(layer);
    });
  }, [isDarkMode]);

  const runAreaSearch = useCallback(() => {
    const map = mapRef.current;
    const bounds = map?.getBounds?.();
    if (!bounds || searchFeedback === 'searching') return;

    if (searchFeedbackTimerRef.current !== null) {
      window.clearTimeout(searchFeedbackTimerRef.current);
      searchFeedbackTimerRef.current = null;
    }

    setSearchFeedback('searching');
    setSearchResultCount(0);
    setWorldwideMenuOpen(false);
    onSearchArea(boundsToPlain(bounds));

    // Keep the feedback visible long enough to feel responsive, then report
    // the latest filtered rows supplied by TournamentHubView.
    searchFeedbackTimerRef.current = window.setTimeout(() => {
      searchFeedbackTimerRef.current = null;
      finishAreaSearch();
      try { map.invalidateSize(false); } catch {}
      window.requestAnimationFrame(() => redraw());
    }, 520);
  }, [finishAreaSearch, onSearchArea, redraw, searchFeedback]);

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return;
    const map = (L as any).map(hostRef.current, {
      center: [34.0522, -118.2437],
      zoom: 7,
      zoomControl: true,
      attributionControl: false,
      minZoom: 3,
      maxZoom: 18,
    });

    (L as any)
      .tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; OpenStreetMap contributors, Tiles style by Humanitarian OpenStreetMap Team hosted by OpenStreetMap France',
      })
      .addTo(map);

    layerRef.current = (L as any).layerGroup().addTo(map);
    mapRef.current = map;

    map.on('zoomend moveend', redraw);
    window.setTimeout(() => {
      try { map.invalidateSize(false); } catch {}
      redraw();
    }, 80);

    return () => {
      try { map.off('zoomend moveend', redraw); } catch {}
      try { map.remove(); } catch {}
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [redraw]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const tilePane = map.getPane?.('tilePane') as HTMLElement | undefined;
    if (tilePane) {
      tilePane.style.filter = isDarkMode
        ? 'brightness(0.50) contrast(0.96) saturate(0.72)'
        : 'none';
      tilePane.style.transition = 'filter 180ms ease';
    }

    try { map.invalidateSize(false); } catch {}
    redraw();
  }, [isDarkMode, redraw]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    redraw();

    if (!fittedRef.current && tournaments.length) {
      const localPoints = tournaments
        .filter((row) => !isWorldwideTournament(row))
        .map((row) => getTournamentMapLatLng(row))
        .filter(Boolean) as Array<{lat:number;lng:number}>;

      if (localPoints.length) {
        fittedRef.current = true;
        const bounds = (L as any).latLngBounds(localPoints.map((p) => [p.lat, p.lng]));
        if (localPoints.length === 1) {
          map.setView([localPoints[0].lat, localPoints[0].lng], 11, { animate: false });
        } else {
          map.fitBounds(bounds, { padding: [28, 28], maxZoom: 10, animate: false });
        }
      } else if (anglerAnchorRef.current) {
        fittedRef.current = true;
        map.setView(
          [anglerAnchorRef.current.lat, anglerAnchorRef.current.lng],
          Math.max(7, Number(map.getZoom?.() ?? 7)),
          { animate: false },
        );
      }
    }
  }, [tournaments, redraw]);

  useEffect(() => {
    const cached = typeof window !== 'undefined' ? readCachedAnglerLocation() : null;
    if (cached) {
      anglerAnchorRef.current = cached;
      if (
        mapRef.current &&
        !fittedRef.current &&
        !(rowsRef.current || []).some(
          (row) => !isWorldwideTournament(row) && !!getTournamentMapLatLng(row),
        )
      ) {
        fittedRef.current = true;
        mapRef.current.setView([cached.lat, cached.lng], 7, { animate: false });
      }
      redraw();
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = Number(position.coords.latitude);
        const lng = Number(position.coords.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        anglerAnchorRef.current = { lat, lng };
        if (
          mapRef.current &&
          !fittedRef.current &&
          !(rowsRef.current || []).some(
            (row) => !isWorldwideTournament(row) && !!getTournamentMapLatLng(row),
          )
        ) {
          fittedRef.current = true;
          mapRef.current.setView([lat, lng], 7, { animate: false });
        }
        redraw();
      },
      () => {},
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 5 * 60 * 1000 },
    );
  }, [redraw]);

  return (
    <div className="relative overflow-visible rounded-[16px] border border-black/10 bg-white/35 dark:border-[rgba(159,140,220,.20)] dark:bg-[#151427]/75">
      <div className="rounded-t-[15px] flex items-center justify-between gap-3 px-3 py-2.5">
        <div className="min-w-0">
          <div className="text-sm font-black">Discover tournaments</div>
          <div className="mt-0.5 text-[11px] font-semibold opacity-55">
            Pan or zoom to explore another area. Worldwide events stay available near you.
          </div>
        </div>
        {areaFilterActive ? (
          <button
            type="button"
            onClick={clearAreaSearch}
            className="shrink-0 rounded-[10px] border border-black/10 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-[0.08em] dark:border-[rgba(159,140,220,.20)]"
          >
            Clear area
          </button>
        ) : null}
      </div>

      <div className="relative">
        <div
          ref={hostRef}
          className="h-[185px] w-full bg-slate-200 dark:bg-[#1B1830]"
          aria-label="Tournament discovery map"
        />

        {worldwideRows.length ? (
          <div ref={worldwideMenuRef} className="absolute right-2 top-2 z-[1200] flex flex-col items-end">
            <button
              type="button"
              onClick={() => {
                if (worldwideRows.length === 1) {
                  openRef.current?.(worldwideRows[0]);
                  return;
                }
                setWorldwideMenuOpen((open) => !open);
              }}
              className={`group flex items-center gap-2 rounded-full border px-2.5 py-1.5 shadow-[0_8px_22px_rgba(15,23,42,.20)] backdrop-blur-xl transition hover:-translate-y-px hover:shadow-[0_10px_26px_rgba(15,23,42,.24)] ${
                worldwideRows.some((row) => ['active', 'live'].includes(String(row.status || '').toLowerCase()))
                  ? 'border-emerald-500/35 bg-white/96 text-emerald-800 dark:border-emerald-300/25 dark:bg-[#11192B]/96 dark:text-emerald-200'
                  : worldwideRows.some((row) => String(row.status || '').toLowerCase() === 'registration_open')
                    ? 'border-amber-500/40 bg-white/96 text-amber-800 dark:border-amber-300/30 dark:bg-[#11192B]/96 dark:text-[#FFE4A1]'
                    : 'border-rose-500/30 bg-white/96 text-rose-700 dark:border-rose-300/20 dark:bg-[#11192B]/96 dark:text-rose-200'
              }`}
              aria-expanded={worldwideRows.length > 1 ? worldwideMenuOpen : undefined}
              aria-label={worldwideRows.length === 1 ? 'Open worldwide tournament' : `Show ${worldwideRows.length} worldwide tournaments`}
            >
              <span className="grid h-6 w-6 place-items-center rounded-full border border-current/15 bg-current/[.07] text-[13px]" aria-hidden="true">🌎</span>
              <span className="text-[10px] font-black uppercase tracking-[.11em]">World</span>
              <span className="grid min-w-[20px] place-items-center rounded-full border border-current/15 bg-current/[.08] px-1.5 py-0.5 text-[9px] font-black">
                {worldwideRows.length}
              </span>
              {worldwideRows.length > 1 ? (
                <span className={`text-[10px] opacity-55 transition-transform ${worldwideMenuOpen ? 'rotate-180' : ''}`}>⌄</span>
              ) : null}
            </button>

            {worldwideMenuOpen && worldwideRows.length > 1 ? (
              <div className="relative z-[1201] mt-2 w-[min(292px,calc(100vw-32px))] overflow-hidden rounded-[18px] border border-black/10 bg-[#FFFDF8]/[.985] shadow-[0_18px_48px_rgba(15,23,42,.28)] backdrop-blur-xl dark:border-white/10 dark:bg-[#101829]/[.985]">
                <div className="border-b border-black/[.07] px-3.5 py-3 dark:border-white/[.08]">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-amber-500/20 bg-amber-400/10 text-sm">🌎</span>
                        <div>
                          <div className="text-[12px] font-black tracking-[-.01em]">Worldwide Tournaments</div>
                          <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[.09em] opacity-45">Global events · available anywhere</div>
                        </div>
                      </div>
                    </div>
                    <span className="rounded-full border border-black/10 bg-black/[.035] px-2 py-1 text-[9px] font-black tabular-nums dark:border-white/10 dark:bg-white/[.05]">
                      {worldwideRows.length} EVENTS
                    </span>
                  </div>
                </div>

                <div className="max-h-[248px] space-y-1.5 overflow-y-auto overscroll-contain p-2">
                  {worldwideRows.map((tournament) => {
                    const visual = worldwideStatusVisual(tournament);
                    const fee = Math.max(0, Math.floor(Number((tournament as any)?.entryFeeShells || 0)));
                    const scoring = String((tournament as any)?.scoringMode || '').replaceAll('_', ' ').trim();
                    const countdown = tournamentCountdownLabel(tournament, Date.now());
                    const approvedCountRaw = Number((tournament as any)?.approvedParticipantCount);
                    const hasApprovedCount = Number.isFinite(approvedCountRaw) && approvedCountRaw >= 0;
                    const approvedCount = hasApprovedCount ? Math.floor(approvedCountRaw) : 0;
                    const maxParticipantsRaw = Number((tournament as any)?.maxParticipants);
                    const hasMaxParticipants = Number.isFinite(maxParticipantsRaw) && maxParticipantsRaw > 0;
                    const anglerCountLabel = hasApprovedCount
                      ? hasMaxParticipants
                        ? `${approvedCount}/${Math.floor(maxParticipantsRaw)} anglers`
                        : `${approvedCount} angler${approvedCount === 1 ? '' : 's'}`
                      : '';
                    return (
                      <button
                        key={tournament.id}
                        type="button"
                        onClick={() => {
                          setWorldwideMenuOpen(false);
                          openRef.current?.(tournament);
                        }}
                        className={`group relative flex w-full items-center gap-3 overflow-hidden rounded-[13px] border px-3 py-2.5 text-left transition hover:-translate-y-px hover:shadow-[0_8px_18px_rgba(15,23,42,.10)] dark:hover:shadow-[0_10px_20px_rgba(0,0,0,.22)] ${visual.rowClass}`}
                      >
                        <span className={`absolute inset-y-2 left-0 w-[3px] rounded-r-full ${visual.accentClass}`} />

                        <span className="min-w-0 flex-1 pl-0.5">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className="truncate text-[11.5px] font-black tracking-[-.01em]">
                              {tournament.name || 'Worldwide tournament'}
                            </span>
                            <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-[3px] text-[8px] font-black uppercase tracking-[.09em] ${visual.chipClass}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${visual.dotClass}`} />
                              {visual.label}
                            </span>
                            {countdown ? (
                              <span className="shrink-0 text-[8px] font-black uppercase tracking-[.04em] text-amber-700 dark:text-[#FFE29A]">
                                ⏱ {countdown}
                              </span>
                            ) : null}
                          </span>

                          <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[8.5px] font-bold uppercase tracking-[.06em] opacity-50">
                            <span>Worldwide</span>
                            {scoring ? (
                              <span className="min-w-0 truncate">• {scoring}</span>
                            ) : null}
                            {anglerCountLabel ? (
                              <>
                                <span>•</span>
                                <span className="shrink-0 normal-case">👥 {anglerCountLabel}</span>
                              </>
                            ) : null}
                            <span>•</span>
                            <span className="shrink-0">{fee > 0 ? `${fee} shells` : 'Free'}</span>
                          </span>
                        </span>

                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-black/[.07] bg-white/55 text-[15px] font-black opacity-45 transition group-hover:translate-x-0.5 group-hover:opacity-75 dark:border-white/[.08] dark:bg-white/[.04]">›</span>
                      </button>
                    );
                  })}
                </div>

                <div className="border-t border-black/[.06] bg-black/[.018] px-3 py-2 text-center text-[8px] font-bold uppercase tracking-[.08em] opacity-40 dark:border-white/[.07] dark:bg-white/[.02]">
                  Open an event for full rules, prizes, and registration
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="rounded-b-[15px] border-t border-black/10 px-3 py-2.5 dark:border-[rgba(159,140,220,.20)]">
        <button
          type="button"
          onClick={runAreaSearch}
          disabled={searchFeedback === 'searching'}
          aria-busy={searchFeedback === 'searching'}
          className={[
            'group relative w-full overflow-hidden rounded-[11px] border border-[#2F80ED]/30 bg-[#2F80ED]/10 px-3 py-2.5 text-xs font-black text-[#2168C9]',
            'shadow-[0_4px_0_rgba(47,128,237,.16)] transition-[transform,box-shadow,background-color,opacity] duration-150',
            'hover:bg-[#2F80ED]/15 active:translate-y-[2px] active:scale-[0.985] active:shadow-[0_1px_0_rgba(47,128,237,.18)]',
            'disabled:cursor-wait disabled:opacity-80 dark:border-[#7057E8]/30 dark:bg-[#7057E8]/[.16] dark:text-[#F4F6FA] dark:shadow-[0_4px_0_rgba(112,87,232,.18)]',
          ].join(' ')}
        >
          <span className="flex items-center justify-center gap-2">
            {searchFeedback === 'searching' ? (
              <>
                <span className="inline-flex items-center gap-[3px]" aria-hidden="true">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-.24s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-.12s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
                </span>
                <span>Searching this area</span>
              </>
            ) : (
              <span>{areaFilterActive ? 'Refresh this area' : 'Search this area'}</span>
            )}
          </span>
        </button>

        {searchFeedback === 'found' ? (
          <div className="mt-2 flex items-center justify-center gap-1.5 rounded-[10px] border border-emerald-500/20 bg-emerald-500/[.08] px-2.5 py-2 text-center text-[10.5px] font-black text-emerald-700 dark:border-emerald-300/15 dark:bg-emerald-300/[.07] dark:text-emerald-200">
            <span aria-hidden="true">✓</span>
            <span>Found {searchResultCount} {searchResultCount === 1 ? 'tournament' : 'tournaments'} in this area.</span>
          </div>
        ) : null}

        {searchFeedback === 'empty' ? (
          <div className="mt-2 rounded-[10px] border border-amber-500/20 bg-amber-400/[.08] px-2.5 py-2 text-center text-[10.5px] font-black text-amber-800 dark:border-amber-300/15 dark:bg-amber-300/[.07] dark:text-amber-200">
            No tournaments found in this area. Pan the map and try another search.
          </div>
        ) : null}
      </div>
    </div>
  );
}
